import type { FC } from "react";

import { AddToWallet } from "@ui/components/AddToWallet";
import { ClubCard } from "@ui/components/ClubCard";
import { Dialog } from "@ui/components/Dialog";
import { TranslationHtmlWrapper } from "@ui/components/TranslationHtmlWrapper/TranslationHtmlWrapper";
import { useDeviceBreakpoint } from "@ui/hooks/useDeviceBreakpoint";
import { useTranslations } from "@ui/providers/useTranslations";

import { useConfig } from "@jti/app-context/useConfig";
import { ProfileFamilyIcon } from "@jti/brand-icons/profile-family";
import { StoreDefaultIcon } from "@jti/brand-icons/store-default";
import type { ContactPersonal } from "@jti/fe-common";
import { getWalletService, isWalletButtonVisible } from "@jti/fe-common";

import styles from "./SharableLoyaltyProfileDialog.module.scss";

type SharableLoyaltyProfileDialogProps = {
  isOpened: boolean;
  userInfo: ContactPersonal;
  userId: string;
  referralCode: string;
  onClose: () => void;
  portalDomNode?: HTMLElement;
  clubCardLogoSrc?: string;
};

export const SharableLoyaltyProfileDialog: FC<SharableLoyaltyProfileDialogProps> = ({
  isOpened,
  userInfo,
  userId,
  referralCode,
  onClose,
  portalDomNode,
  clubCardLogoSrc,
}) => {
  const { __ } = useTranslations();
  const { breakpoint } = useDeviceBreakpoint();
  const { walletButtonsVisibility } = useConfig();
  const isMobile = breakpoint === "mobile";
  const walletService = getWalletService();
  const walletButtonVisible = isWalletButtonVisible(walletButtonsVisibility, walletService);

  return (
    <Dialog
      dataTestId="sharableLoyaltyProfileDialog"
      isOpened={isOpened}
      closable={true}
      dialogClassName={styles.dialogContainer}
      className={styles.dialog}
      onClose={onClose}
      portalDomNode={portalDomNode}
      fullscreen={isMobile}
    >
      <div className={styles.dialogContent}>
        <h1 className={styles.title}>{__("profile.personal_details.sharable_loyalty_profile.title")}</h1>

        <div className={styles.mainContent}>
          <ClubCard
            firstName={userInfo.firstname}
            lastName={userInfo.lastname}
            email={userInfo.email}
            dateOfBirth={userInfo.dob ?? ""}
            userId={userId}
            referralCode={referralCode}
            logoSrc={clubCardLogoSrc}
          />
          <div className={styles.instructionsWithButtons}>
            <div className={styles.instructionsContainer}>
              <p className={styles.instructionTitle}>
                {__("profile.personal_details.sharable_loyalty_profile.instructions_title")}
              </p>

              <div className={styles.instructionItem}>
                <div className={styles.iconContainer}>
                  <ProfileFamilyIcon className={styles.icon} />
                </div>
                <p className={styles.instructionText}>
                  {__("profile.personal_details.sharable_loyalty_profile.employee_instruction")}
                </p>
              </div>

              <div className={styles.instructionItem}>
                <div className={styles.iconContainer}>
                  <StoreDefaultIcon className={styles.icon} />
                </div>
                <p className={styles.instructionText}>
                  {__("profile.personal_details.sharable_loyalty_profile.store_instruction")}
                </p>
              </div>

              <TranslationHtmlWrapper
                className={styles.knowMoreLink}
                text="profile.personal_details.sharable_loyalty_profile.know_more"
                htmlData={[]}
              />
            </div>
            {isMobile && walletButtonVisible && (
              <div className={styles.saveCardContainer}>
                <p className={styles.instructionTitle}>
                  {__("profile.personal_details.sharable_loyalty_profile.save_card")}
                </p>
                <AddToWallet walletService={walletService} />
              </div>
            )}
          </div>
        </div>
        <p className={styles.agreementText}>
          {__("profile.personal_details.sharable_loyalty_profile.agreement_notification")}
        </p>
      </div>
    </Dialog>
  );
};
